const IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const MAX_IMAGE_BYTES = 24 * 1024 * 1024;
const DRIVE_ID_PATTERN = /^[a-zA-Z0-9_-]{20,}$/;

export function getUploadErrorMessage(error) {
  const message = [
    error?.message,
    error?.error_description,
    error?.response?.data?.detail,
    error?.response?.data?.message,
    error?.response?.data?.error,
  ].filter(Boolean).join(" ");

  const lower = message.toLowerCase();
  if (!message) return "Upload failed: Unknown error. Please try again.";
  if (lower.includes("bucket") && lower.includes("not")) return "Upload failed: Supabase bucket was not found. Check bucket mff-media.";
  if (lower.includes("row-level security") || lower.includes("policy") || lower.includes("permission") || lower.includes("unauthorized")) {
    return "Upload failed: Supabase Storage policy denied this upload. Admin upload policy may need to be added.";
  }
  if (lower.includes("network") || lower.includes("failed to fetch") || lower.includes("cors")) {
    return "Upload failed: Network/CORS issue while uploading to Supabase.";
  }
  return `Upload failed: ${message}`;
}

export function validateImageFile(file) {
  if (!file) throw new Error("No file selected.");
  const type = String(file.type || "").toLowerCase();
  const ext = file.name.split(".").pop()?.toLowerCase();
  const looksSupported = IMAGE_TYPES.has(type) || ["jpg", "jpeg", "png", "webp"].includes(ext);
  if (!looksSupported) throw new Error("Unsupported file type. Please upload JPG, JPEG, PNG, or WebP.");
  if (file.size > MAX_IMAGE_BYTES) throw new Error("File size too large. Please upload an image under 24 MB.");
}

export function safeFileName(name = "image") {
  const parts = String(name).split(".");
  const ext = (parts.length > 1 ? parts.pop() : "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const base = parts.join(".") || "image";
  return `${base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 70) || "image"}.${ext}`;
}

export function extractGoogleDriveFolderId(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    const folderMatch = url.pathname.match(/\/folders\/([a-zA-Z0-9_-]+)/i);
    if (folderMatch?.[1]) return folderMatch[1];
  } catch {
    // Plain text is handled by the caller; single raw IDs are treated as file IDs.
  }
  return "";
}

export function extractGoogleDriveFileId(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  const fileMatch = raw.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  if (fileMatch?.[1]) return fileMatch[1];
  const queryMatch = raw.match(/[?&]id=([a-zA-Z0-9_-]{20,})/i);
  if (queryMatch?.[1]) return queryMatch[1];
  return DRIVE_ID_PATTERN.test(raw) ? raw : "";
}

export function googleDriveMediaUrls(fileId, { mediaType = "image" } = {}) {
  const id = encodeURIComponent(String(fileId || "").trim());
  const isVideo = mediaType === "video";
  return {
    mediaType: isVideo ? "video" : "image",
    thumbnailUrl: `https://drive.google.com/thumbnail?id=${id}&sz=${isVideo ? "w1000" : "w2000"}`,
    previewUrl: isVideo
      ? `https://drive.google.com/file/d/${id}/preview`
      : `https://drive.google.com/thumbnail?id=${id}&sz=w2000`,
    downloadUrl: `https://drive.google.com/uc?export=download&id=${id}`,
  };
}

export function isGoogleDriveUrl(input) {
  return /(^[a-zA-Z0-9_-]{20,}$|drive\.google\.com)/i.test(String(input || "").trim());
}

export async function compressImageFile(file, { maxEdge = 2000, quality = 0.84 } = {}) {
  validateImageFile(file);
  if (file.type === "image/gif") return file;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const longest = Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height);
        const scale = longest > maxEdge ? maxEdge / longest : 1;
        if (scale >= 1 && file.size < 2.5 * 1024 * 1024) {
          resolve(new File([file], safeFileName(file.name), { type: file.type || "image/jpeg" }));
          return;
        }

        const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
        const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d", { alpha: false });
        context.drawImage(image, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(new File([file], safeFileName(file.name), { type: file.type || "image/jpeg" }));
            return;
          }
          const jpgName = safeFileName(file.name).replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], jpgName, { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      };
      image.onerror = () => resolve(new File([file], safeFileName(file.name), { type: file.type || "image/jpeg" }));
      image.src = String(reader.result || "");
    };
    reader.onerror = () => resolve(new File([file], safeFileName(file.name), { type: file.type || "image/jpeg" }));
    reader.readAsDataURL(file);
  });
}

export function normalizeExternalImageUrl(input) {
  const raw = String(input || "").trim();
  if (!raw) throw new Error("Please paste an image URL or Google Drive file ID.");

  if (extractGoogleDriveFolderId(raw) || /drive\.google\.com\/drive\/folders\//i.test(raw) || /\/folders\//i.test(raw)) {
    throw new Error("Use Gallery Folders / Albums import section for folder links.");
  }

  const driveId = extractGoogleDriveFileId(raw);
  if (driveId) {
    return googleDriveMediaUrls(driveId).previewUrl;
  }

  try {
    const url = new URL(raw);
    if (!["http:", "https:"].includes(url.protocol)) throw new Error();
    return url.toString();
  } catch {
    throw new Error("Please paste a valid direct image URL or Google Drive single image file link.");
  }
}

export function validateImageUrl(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = window.setTimeout(() => {
      image.src = "";
      reject(new Error("Image URL could not be loaded. If this is a Google Drive file, make sure it is public: Anyone with the link can view."));
    }, 12000);
    image.onload = () => {
      window.clearTimeout(timer);
      resolve(url);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error("Image URL could not be loaded. Google Drive file must be public: Anyone with the link can view."));
    };
    image.referrerPolicy = "no-referrer";
    image.src = url;
  });
}

export async function normalizeAndValidateExternalImage(input) {
  const url = normalizeExternalImageUrl(input);
  const isDriveFile = isGoogleDriveUrl(input) || /drive\.google\.com\/thumbnail/i.test(url);
  try {
    await validateImageUrl(url);
  } catch (error) {
    if (!isDriveFile) throw error;
    // Google Drive thumbnails sometimes become available shortly after sharing
    // permissions propagate. Save the generated thumbnail URL instead of
    // rejecting a valid Drive file link during admin data entry.
    console.warn("[ADMIN_DRIVE_IMAGE_VALIDATION_SKIPPED]", {
      message: "Google Drive file must be shared as Anyone with the link -> Viewer.",
    });
  }
  return url;
}
